/**
 * Flow designer canvas (P-1.4 + P-1.5).
 *
 * Implements FLOWS.md § 11.10 visual language and the node editing behavior:
 *   - dotted grid background, minimap, zoom controls, snap-to-grid
 *   - animated bezier edges with error variants
 *   - drag from palette → create node (via HTML5 drag-and-drop + dataTransfer)
 *   - handle-to-handle drag → create edge (onConnect writes into the source
 *     node's pointer field via editorStore.connectEdge)
 *   - select node → side panel opens
 *   - delete key → remove selected node / edge
 */

import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  type NodeChange,
  type EdgeChange,
  type Connection,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useEffect, type DragEvent } from "react";
import { nodeTypes } from "./nodes/nodeTypes";
import { edgeTypes } from "./edges/DeletableEdge";
import {
  useEditorStore,
  type CanvasNode,
  type CanvasEdge,
} from "./editorStore";
import type { NodeType } from "../../types/flow";
import { isValidConnection as checkConnection } from "./connectionValidator";
import { registerCanvasFocus } from "./canvasFocus";

export function Canvas() {
  return (
    <div className="relative flex-1 min-h-0">
      <CanvasInner />
    </div>
  );
}

function CanvasInner() {
  const nodes = useEditorStore((s) => s.nodes);
  const edges = useEditorStore((s) => s.edges);
  const setNodes = useEditorStore((s) => s.setNodes);
  const setEdges = useEditorStore((s) => s.setEdges);
  const selectNode = useEditorStore((s) => s.selectNode);
  const openDrawer = useEditorStore((s) => s.openDrawer);
  const openAddMenu = useEditorStore((s) => s.openAddMenu);
  const connectEdge = useEditorStore((s) => s.connectEdge);
  const deleteNode = useEditorStore((s) => s.deleteNode);
  const deleteEdge = useEditorStore((s) => s.deleteEdge);
  const addNode = useEditorStore((s) => s.addNode);

  const { screenToFlowPosition, setCenter, getZoom } = useReactFlow();

  // Expose a "focus on this node" channel for the Toolbar issues popover.
  // Re-registers whenever react-flow's instance changes so setCenter always
  // points at the live viewport controller.
  useEffect(() => {
    registerCanvasFocus((nodeId: string) => {
      const node = useEditorStore.getState().nodes.find((n) => n.id === nodeId);
      if (!node) return;
      // Approximate node center so it lands mid-viewport. Node widths in our
      // palette run ~240px; 120px is a decent visual midpoint.
      const cx = node.position.x + 120;
      const cy = node.position.y + 60;
      const zoom = Math.max(getZoom(), 0.9);
      setCenter(cx, cy, { zoom, duration: 400 });
      useEditorStore.getState().selectNode(nodeId);
    });
    return () => registerCanvasFocus(null);
  }, [setCenter, getZoom]);

  // Keyboard: Delete / Backspace removes selected node(s) or edge(s).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const selectedNode = nodes.find((n) => n.selected);
      const selectedEdge = edges.find((ed) => ed.selected);
      if (selectedEdge) {
        deleteEdge(selectedEdge.id);
      } else if (selectedNode) {
        deleteNode(selectedNode.id);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nodes, edges, deleteNode, deleteEdge]);

  const handleNodesChange = useCallback(
    (changes: NodeChange<CanvasNode>[]) => {
      setNodes((curr) => applyNodeChanges(changes, curr));
      // Persist position changes back into the store's positions map so the
      // layout round-trips through rebuilds. The subscription in
      // FlowEditorPage debounces the actual sidecar write to localStorage.
      const posChanges = changes.filter(
        (c): c is Extract<NodeChange<CanvasNode>, { type: "position" }> =>
          c.type === "position" && c.position !== undefined && c.dragging === false,
      );
      if (posChanges.length > 0) {
        useEditorStore.setState((s) => ({
          ...s,
          positions: posChanges.reduce(
            (acc, c) => ({ ...acc, [c.id]: c.position! }),
            s.positions,
          ),
        }));
      }
      // Process every select event in order — when the user clicks from one
      // node to another, react-flow emits a deselect for the old node AND a
      // select for the new node in the same change batch. Picking only the
      // first event would leave the drawer empty.
      for (const c of changes) {
        if (c.type === "select") {
          selectNode(c.selected ? c.id : null);
        }
      }
    },
    [setNodes, selectNode],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<CanvasEdge>[]) => {
      setEdges((curr) => applyEdgeChanges(changes, curr));
    },
    [setEdges],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || !connection.sourceHandle) return;
      connectEdge(connection.source, connection.sourceHandle, connection.target);
    },
    [connectEdge],
  );

  const handleIsValid = useCallback(
    (connection: Connection | CanvasEdge) => {
      const conn: Connection = {
        source: connection.source ?? null,
        target: connection.target ?? null,
        sourceHandle: connection.sourceHandle ?? null,
        targetHandle: connection.targetHandle ?? null,
      };
      return checkConnection(
        conn,
        (id) => nodes.find((n) => n.id === id)?.data.nodeType,
        edges,
      );
    },
    [nodes, edges],
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData("application/costplus-node-type");
      if (!nodeType) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      addNode(nodeType as NodeType, position);
    },
    [addNode, screenToFlowPosition],
  );

  return (
    <ReactFlow<CanvasNode, CanvasEdge>
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onConnect={handleConnect}
      isValidConnection={handleIsValid}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onNodeDoubleClick={(_evt, node) => {
        // Double-click = open the configuration drawer for any node. The page
        // builder for a checkout_page is reachable via the "Open page builder"
        // button inside that drawer.
        openDrawer(node.id);
      }}
      onPaneClick={() => openDrawer(null)}
      onPaneContextMenu={(evt) => {
        // Right-click on empty canvas → open the contextual add-node menu.
        evt.preventDefault();
        const e = evt as unknown as MouseEvent;
        openAddMenu(e.clientX, e.clientY, null);
      }}
      snapToGrid
      snapGrid={[16, 16]}
      fitView
      fitViewOptions={{ padding: 0.2, maxZoom: 1.25 }}
      proOptions={{ hideAttribution: true }}
      className="bg-neutral-50 dark:bg-neutral-950"
      deleteKeyCode={null}
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={16}
        size={1}
        className="!text-neutral-300 dark:!text-neutral-800"
      />
      <Controls
        position="bottom-left"
        showInteractive={false}
        className="!bg-white/90 dark:!bg-neutral-900/90 !border !border-neutral-200 dark:!border-neutral-800 !rounded-md"
      />
      <MiniMap
        position="bottom-right"
        pannable
        zoomable
        className="!bg-white/90 dark:!bg-neutral-900/90 !border !border-neutral-200 dark:!border-neutral-800 !rounded-md"
        nodeColor={(n) => {
          const type = (n.data as { nodeType?: string } | undefined)?.nodeType;
          switch (type) {
            case "trigger":
              return "#22c55e";
            case "condition":
            case "switch":
            case "ab_test":
            case "schedule":
              return "#3b82f6";
            case "checkout_page":
              return "#a855f7";
            case "capture_policy":
            case "delay":
            case "fraud_check":
              return "#f97316";
            default:
              return "#737373";
          }
        }}
      />
      <style>{`
        .react-flow__edge.edge-error .react-flow__edge-path {
          stroke: #ef4444;
          stroke-dasharray: 4 4;
        }
        .react-flow__edge.animated .react-flow__edge-path {
          stroke-dasharray: 5;
          animation: dashdraw 0.5s linear infinite;
        }
        .react-flow__node.untraced { opacity: 0.35; }
        .react-flow__node.traced { outline: 2px solid #22c55e; outline-offset: 2px; border-radius: 8px; }
        .react-flow__edge.edge-traced .react-flow__edge-path {
          stroke: #22c55e;
          stroke-width: 2;
        }
        /* Per-output "+" badge — discoverable on hover. The "intent" travel
           gap between the node body and the badge is bridged with a delayed
           fade-out: cursor leaves the node, badge stays visible for 250ms,
           and during that grace period :hover on the button itself keeps it
           visible permanently (until the cursor leaves the button). */
        .cp-add-handle-btn {
          opacity: 0;
          transition: opacity 120ms ease;
          transition-delay: 250ms;
          z-index: 10;
        }
        .react-flow__node:hover .cp-add-handle-btn,
        .cp-add-handle-btn:hover,
        .cp-add-handle-btn:focus-visible {
          opacity: 1;
          transition-delay: 0ms;
        }
      `}</style>
    </ReactFlow>
  );
}
